/**
 * lib/badges/resolve.ts — the CANONICAL server-side badge resolver.
 *
 * `resolveBadges()` is the single code path that turns a product row + its best
 * retailer offer + analytics signals into the canonical `Badge[]` array defined
 * in docs/Page-Types-And-Templates.md §3.1. Every API route (products, collections,
 * search) is meant to call THIS function and emit `badges: Badge[]` on each
 * product (spec §6), so every surface renders the same badges for the same
 * product — killing the four-different-price-drop-definitions bug documented in
 * spec §2.4.
 *
 * WHY IT LIVES IN lib/ (not components/badges/config.ts):
 *   - config.ts owns the PRESENTATION contract (BADGE_CONFIGS, makeBadge, the
 *     `Badge` type) and the legacy client engine `determineBadges()`. It is a
 *     component-tree module.
 *   - resolve.ts owns the SERVER RESOLUTION contract. It imports the shared
 *     presentation tokens from config.ts (single source of truth for styling)
 *     and the ordering helpers from select.ts, so a resolver-built badge renders
 *     byte-identically to a client-built one.
 *
 * PURITY: this module is pure and framework-free — no Supabase, no React, no I/O.
 * Analytics-derived inputs (popularity score / velocity + their thresholds) are
 * passed in as `signals`; the caller loads them from the cron-materialized
 * `products.popularity_score` / `popularity_velocity` columns (see
 * lib/badges/popularity.ts). This keeps the rule set unit-testable in the node
 * jest env with zero mocks.
 *
 * WIRED (item 64): the three API routes (products, collections, search) emit
 * `badges: Badge[]` via the server adapter `lib/badges/from-row.ts`
 * (`resolveBadgesForRow`), which calls this resolver. Badges are Real-or-Hidden
 * from existing price/spec columns today; `best_seller` + `trending` stay dark
 * until the DORMANT popularity cron materializes `products.popularity_score`.
 * Per-surface cut-over of the render components to read `product.badges` (vs the
 * legacy client `determineBadges` path) is the incremental follow-up in spec §6.
 *
 * `seen_before` is NEVER produced here — it is a client-only overlay layered on
 * AFTER the server array (spec §4.3, see `withSeenBefore` in ./select).
 */

import { makeBadge, type Badge, type BadgeType } from '@/components/badges/config'
import {listedDiscountMeta,type ListedOfferEvidence} from './price-evidence'
import {computeVerdict,type PriceHistoryStats} from '@/components/deal-verdict/compute'
import { sortBadges } from '@/components/badges/select'

/** Product-level fields the resolver reads (all optional / nullable-tolerant). */
export interface ResolveProductInput {
  /** Current best price across offers, in cents. */
  lowestPriceCents?: number | null
  /** Legacy 30-day average, retained for shape compatibility; not price evidence. */
  averagePrice30dCents?: number | null
  /** Legacy historical-low field; never promoted to verified history. */
  lowestPriceEverCents?: number | null
  /** ISO timestamp; drives the `new` badge (<= 30 days). */
  createdAt?: string | null
  rating?: number | null
  reviews?: number | null
  energyStar?: boolean | null
}

/**
 * The single best offer for the product (already chosen by the caller — usually
 * the lowest-price in-stock offer). Product-level `deal` / `low_stock` badges are
 * derived from it (spec §4.4: offer-level OffersV2 badges stay separate).
 */
export interface ResolveOfferInput extends ListedOfferEvidence {
  /** This offer's price, in cents (price-drop offer fallback). */
  priceCents?: number | null
  /** This offer's strike-through / "was" price, in cents. */
  originalPriceCents?: number | null
  /** Retailer deal marker (e.g. Amazon Lightning). Presence drives `deal`. */
  dealBadge?: string | null
  /** ISO timestamp the deal ends; carried into `deal` meta when present. */
  dealEndsAt?: string | null
  /** Remaining stock; `0 < qty < 5` drives `low_stock`. */
  availabilityMaxQty?: number | null
}

/**
 * Analytics-derived signals + their thresholds. Supplied by the caller from the
 * cron-materialized popularity columns. When absent, `best_seller` / `trending`
 * simply do not fire (honest: no analytics loaded → no analytics badges).
 */
export interface ResolveSignalsInput {
  /** `products.popularity_score` — trailing-30d non-bot outbound clicks. */
  popularityScore?: number | null
  /** Site-wide cutoff a product must meet/exceed to earn `best_seller`. */
  bestSellerThreshold?: number | null
  /** `products.popularity_velocity` — last-7d / prior-7d click ratio. */
  popularityVelocity?: number | null
  /** Minimum velocity for `trending` (default DEFAULT_TRENDING_MIN_VELOCITY). */
  trendingThreshold?: number | null
  /** Volume floor so a 2→4 click blip cannot fire `trending` (default DEFAULT_TRENDING_MIN_CLICKS). */
  trendingMinClicks?: number | null
}

export interface ResolveBadgesInput {
  product: ResolveProductInput
  bestOffer?: ResolveOfferInput | null
  signals?: ResolveSignalsInput | null
  /** Caller must supply admissible ledger stats, never denormalized legacy columns. */
  verifiedHistory?: {source:'verified-price-ledger';stats:PriceHistoryStats;asOf?:Date}|null
}

// Locked thresholds (spec §4.1). Continuity with the retired client rule
// (`clickCount > 100`) informs the best-seller floor; the API may override with a
// live-distribution percentile (see docs/Page-Types-And-Templates.md).
export const DEFAULT_BEST_SELLER_MIN_CLICKS = 100
export const DEFAULT_TRENDING_MIN_VELOCITY = 1.5 // +50% week-over-week
export const DEFAULT_TRENDING_MIN_CLICKS = 10

const NEW_PRODUCT_MAX_AGE_DAYS = 30
const TOP_RATED_MIN_RATING = 4.5
const TOP_RATED_MIN_REVIEWS = 100
const PREMIUM_MIN_PRICE_CENTS = 200000 // $2,000
const PREMIUM_MIN_RATING = 4.5
const LOW_STOCK_MAX_QTY = 5

function isPositiveInt(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0
}

/**
 * Resolve the canonical badge set for one product.
 *
 * Deterministic: identical input → identical ordered output (spec §4.2), because
 * the final `sortBadges` orders strictly by config priority regardless of the
 * order rules push. Never emits `seen_before` (client overlay only, §4.3).
 */
export function resolveBadges(input: ResolveBadgesInput): Badge[] {
  const { product, bestOffer, signals } = input
  const offer = bestOffer ?? null
  const out: Badge[] = []

  // Historical comparisons require an explicit, admissible ledger contract.
  // Current and reference prices always belong to the same eligible offer.
  const current=offer?.priceCents
  const currentEligible=isPositiveInt(current)&&Number.isSafeInteger(current)&&!offer.priceStale&&!offer.isPriceUnknown&&offer.inStock!==false&&(offer.status==null||offer.status==='active')&&(!isPositiveInt(product.lowestPriceCents)||product.lowestPriceCents===current)
  const history=input.verifiedHistory?.source==='verified-price-ledger'&&currentEligible
    ? computeVerdict(current,input.verifiedHistory.stats,input.verifiedHistory.asOf):null
  const historyLabel=history?.tier==='all-time-low'?'Lowest tracked price':history?.tier==='below-average'?'Below tracked average':null
  const discount=listedDiscountMeta(offer,product.lowestPriceCents)
  if(historyLabel)out.push({...makeBadge('price_drop'),label:historyLabel})
  else if(discount)out.push(makeBadge('price_drop',discount))

  // 2. deal (priority 2) — product-level "this product has a retailer deal".
  if (offer?.dealBadge) {
    out.push(
      makeBadge('deal', offer.dealEndsAt ? { dealEndsAt: offer.dealEndsAt } : undefined)
    )
  }

  // 3. top_rated (priority 3)
  if (
    isPositiveInt(product.rating) &&
    product.rating >= TOP_RATED_MIN_RATING &&
    isPositiveInt(product.reviews) &&
    product.reviews > TOP_RATED_MIN_REVIEWS
  ) {
    out.push(makeBadge('top_rated'))
  }

  // 4. best_seller (priority 4) — popularity_score at/above the site cutoff.
  const bestSellerThreshold = signals?.bestSellerThreshold ?? DEFAULT_BEST_SELLER_MIN_CLICKS
  if (
    isPositiveInt(signals?.popularityScore) &&
    (signals!.popularityScore as number) >= bestSellerThreshold
  ) {
    out.push(makeBadge('best_seller'))
  }

  // 5. premium (priority 5)
  if (
    isPositiveInt(product.lowestPriceCents) &&
    product.lowestPriceCents > PREMIUM_MIN_PRICE_CENTS &&
    isPositiveInt(product.rating) &&
    product.rating >= PREMIUM_MIN_RATING
  ) {
    out.push(makeBadge('premium'))
  }

  // 6. trending (priority 6) — velocity above threshold, with a volume floor so
  // a low-traffic blip cannot fire it.
  const trendingThreshold = signals?.trendingThreshold ?? DEFAULT_TRENDING_MIN_VELOCITY
  const trendingMinClicks = signals?.trendingMinClicks ?? DEFAULT_TRENDING_MIN_CLICKS
  if (
    isPositiveInt(signals?.popularityVelocity) &&
    (signals!.popularityVelocity as number) >= trendingThreshold &&
    isPositiveInt(signals?.popularityScore) &&
    (signals!.popularityScore as number) >= trendingMinClicks
  ) {
    out.push(makeBadge('trending'))
  }

  // 7. energy_star (priority 7)
  if (product.energyStar === true) {
    out.push(makeBadge('energy_star'))
  }

  // 8. new (priority 8) — created within the last 30 days.
  if (product.createdAt) {
    const created = new Date(product.createdAt).getTime()
    if (Number.isFinite(created)) {
      const ageDays = (Date.now() - created) / (1000 * 60 * 60 * 24)
      if (ageDays >= 0 && ageDays <= NEW_PRODUCT_MAX_AGE_DAYS) {
        out.push(makeBadge('new'))
      }
    }
  }

  // 9. low_stock (priority 9) — 0 < qty < 5, from the best offer.
  if (
    isPositiveInt(offer?.availabilityMaxQty) &&
    (offer!.availabilityMaxQty as number) < LOW_STOCK_MAX_QTY
  ) {
    out.push(makeBadge('low_stock', { stockQty: offer!.availabilityMaxQty as number }))
  }

  // seen_before (priority 10) is intentionally never emitted here (spec §4.3).

  // Sort ascending by priority + dedup (spec §4.2). Ordering is what guarantees
  // sitewide consistency, so it is applied here, once, in the resolver.
  return sortBadges(out)
}

/** Convenience re-exports so callers can `import { resolveBadges, Badge } from '@/lib/badges'`. */
export type { Badge, BadgeType }
